import axios, { AxiosError, type CancelTokenSource, type InternalAxiosRequestConfig } from 'axios';
import { Session, buildAuthRedirectToUrl } from '@om/shared-auth';
import { useAuthStore } from '@/lib/stores/auth.store';

const isAuthDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const searchParams = new URLSearchParams(window.location.search);
  return import.meta.env.DEV || searchParams.get('authDebug') === '1' || window.localStorage.getItem('authDebug') === '1';
};

const authDebugLog = (message: string, payload?: Record<string, unknown>) => {
  if (!isAuthDebugEnabled()) {
    return;
  }

  if (payload) {
    console.info('[auth-debug][apiClient]', message, payload);
    return;
  }

  console.info('[auth-debug][apiClient]', message);
};

// Handle both Vite and test environments

// Prefer Vite env, else use proxy path (never hardcode port)
const API_BASE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) || 
                     (typeof process !== 'undefined' && process.env?.VITE_API_BASE_URL) || 
                     '/api';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isLoggingOut = false;
const pendingRequests: CancelTokenSource[] = [];

const removePendingRequest = (cancelToken?: unknown): void => {
  if (!cancelToken) {
    return;
  }

  const index = pendingRequests.findIndex((source) => source.token === cancelToken);
  if (index > -1) {
    pendingRequests.splice(index, 1);
  }
};

export const setLoggingOut = (value: boolean) => {
  isLoggingOut = value;
  authDebugLog('set-logging-out', { value });
  if (value) {
    // Cancel all pending requests when logging out
    pendingRequests.forEach(source => {
      try {
        source.cancel('User logged out');
      } catch {
        // Ignore errors when cancelling
      }
    });
    pendingRequests.length = 0;
  }
};

const redirectToAuthPage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.location.pathname.startsWith('/auth')) {
    authDebugLog('redirect-skipped-already-on-auth');
    return;
  }

  authDebugLog('redirecting-to-auth', { fromPath: window.location.pathname });
  window.location.replace(buildAuthRedirectToUrl(window.location.href));
};

const clearAuthAndSignOut = async (): Promise<void> => {
  try {
    useAuthStore.getState().clearAuth();
    await Session.signOut();
  } catch {
    // Ignore sign out errors - session may already be invalid
  }
};

export const signOutAndRedirectToAuth = async (): Promise<void> => {
  authDebugLog('signout-and-redirect-start');
  setLoggingOut(true);
  await clearAuthAndSignOut();
  setLoggingOut(false);
  redirectToAuthPage();
};

export const resetUnauthorizedLogoutGuard = (): void => {
  // No-op: kept for compatibility with existing callers.
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (isLoggingOut && !config.url?.includes('/auth/logout')) {
      authDebugLog('request-cancelled-due-to-logout', { url: config.url });
      throw new axios.Cancel('Request cancelled due to logout');
    }

    const cancelToken = axios.CancelToken.source();
    config.cancelToken = cancelToken.token;
    pendingRequests.push(cancelToken);

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => {
    removePendingRequest(response.config.cancelToken);
    return response;
  },
  async (error: AxiosError | unknown) => {
    const axiosError = error as AxiosError;
    removePendingRequest(axiosError?.config?.cancelToken);

    if (isLoggingOut || axios.isCancel(error)) {
      authDebugLog('response-ignored-logout-or-cancelled', {
        url: axiosError?.config?.url,
        isLoggingOut,
        isCancelled: axios.isCancel(error),
      });
      return Promise.reject(error);
    }

    if (axiosError.response?.status === 401) {
      authDebugLog('response-401', {
        url: axiosError?.config?.url,
      });
    }

    return Promise.reject(error);
  }
);

// SuperTokens automatically adds XHR interceptors; no need to add axios ones separately
export const initApiClient = () => {
  // Session.addAxiosInterceptors is intentionally omitted — SuperTokens uses
  // XMLHttpRequest interceptors by default which already cover axios requests.
  // Calling addAxiosInterceptors would cause a "cannot use both at same time" warning.
};

type ApiErrorItem = string | {
  message?: string;
  msg?: string;
  details?: string;
  path?: string;
  field?: string;
  param?: string;
};

type ApiErrorPayload = {
  message?: string;
  error?: string;
  details?: ApiErrorItem[];
  errors?: ApiErrorItem[];
  requestId?: string;
  logId?: string;
};

const getHeaderValue = (headers: unknown, headerName: string): string | undefined => {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }

  const normalizedHeaderName = headerName.toLowerCase();
  const headerMap = headers as Record<string, unknown>;
  const headerValue = headerMap[normalizedHeaderName] ?? headerMap[headerName];

  if (typeof headerValue === 'string' && headerValue.trim()) {
    return headerValue;
  }

  if (Array.isArray(headerValue) && headerValue.length > 0 && typeof headerValue[0] === 'string') {
    return headerValue[0];
  }

  return undefined;
};

const extractLogId = (axiosError: AxiosError<ApiErrorPayload>): string | undefined => {
  const responseData = axiosError.response?.data;
  const payloadLogId = typeof responseData?.logId === 'string' ? responseData.logId.trim() : '';
  if (payloadLogId) {
    return payloadLogId;
  }

  const payloadRequestId = typeof responseData?.requestId === 'string' ? responseData.requestId.trim() : '';
  if (payloadRequestId) {
    return payloadRequestId;
  }

  return (
    getHeaderValue(axiosError.response?.headers, 'x-log-id')
    || getHeaderValue(axiosError.response?.headers, 'x-request-id')
  );
};

const withLogId = (message: string, logId?: string): string => {
  if (!logId) {
    return message;
  }

  const normalizedMessage = message.toLowerCase();
  if (normalizedMessage.includes('logid:') || normalizedMessage.includes('requestid:') || message.includes(logId)) {
    return message;
  }

  return `${message} (LogId: ${logId})`;
};

const toValidationMessages = (items: ApiErrorItem[] | undefined): string[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  const messages: string[] = [];

  for (const item of items) {
    if (typeof item === 'string') {
      const normalized = item.trim();
      if (normalized) {
        messages.push(normalized);
      }
      continue;
    }

    if (!item || typeof item !== 'object') {
      continue;
    }

    const rawMessage = item.message ?? item.msg ?? item.details;
    const message = typeof rawMessage === 'string' ? rawMessage.trim() : '';

    const rawField = item.path ?? item.field ?? item.param;
    const field = typeof rawField === 'string' ? rawField.trim() : '';

    if (message && field && !message.toLowerCase().includes(field.toLowerCase())) {
      messages.push(`${field}: ${message}`);
      continue;
    }

    if (message) {
      messages.push(message);
      continue;
    }

    if (field) {
      messages.push(`${field} is invalid`);
    }
  }

  return Array.from(new Set(messages));
};

export const handleApiError = (error: unknown): string => {
  const axiosError = error as AxiosError<ApiErrorPayload>;
  const logId = extractLogId(axiosError);

  if (axiosError?.response) {
    const { data } = axiosError.response;
    const details = [
      ...toValidationMessages(data?.details),
      ...toValidationMessages(data?.errors),
    ];

    if (details.length > 0) {
      const joinedDetails = details.join(', ');
      const genericMessage = data?.message ?? data?.error;
      if (genericMessage && /validation failed/i.test(genericMessage)) {
        return withLogId(`${genericMessage}: ${joinedDetails}`, logId);
      }
      return withLogId(genericMessage ? `${genericMessage}: ${joinedDetails}` : joinedDetails, logId);
    }

    return withLogId(data?.message || data?.error || 'An error occurred', logId);
  }

  if (axiosError?.request) {
    return withLogId('Unable to connect to server', logId);
  }

  return withLogId((axiosError?.message as string) || 'An unexpected error occurred', logId);
};